import inlineStyles from 'juice';
import { SanitizeableChannels } from 'lib-shared-legacy-types';
import pipe from 'lodash/fp/pipe';
import sanitize from 'sanitize-html';

import { sanitizationProfiles } from './profiles';
import { SanitizationCategory } from './types';
import {
  channelToSanitizationProfile,
  removePlaceholderParagraphs,
} from './utils';

type SupportedChannels = SanitizeableChannels | 'notes' | 'bulk';

const safeInlineStyles = (html: string): string => {
  try {
    return inlineStyles(html);
  } catch (e) {
    return html;
  }
};

export class SanitizeHTML {
  public static profiles = sanitizationProfiles;

  public static sanitizerForProfile(profile: SanitizationCategory) {
    return (html: string): string => SanitizeHTML.byProfile(html, profile);
  }

  public static sanitizerForChannel(channel: SupportedChannels) {
    return (html: string): string => SanitizeHTML.byChannel(html, channel);
  }

  public static byChannel(html: string, channel?: string | null): string {
    const profile: SanitizationCategory =
      (channel && channelToSanitizationProfile[channel]) || 'default';
    return SanitizeHTML.sanitize(html, profile);
  }

  public static byProfile(html: string, profile: SanitizationCategory): string {
    return SanitizeHTML.sanitize(html, profile);
  }

  private static sanitize(html: string, profile: SanitizationCategory): string {
    const inlineCSS = profile === 'email';
    return SanitizeHTML.customSanitize(
      html,
      sanitizationProfiles[profile],
      inlineCSS,
    );
  }

  public static customSanitize(
    html: string,
    sanitizationOptions: Parameters<typeof sanitize>[1],
    inlineCSS: boolean = false,
  ): string {
    const sanitizeHTML = (html: string): string =>
      sanitize(html, sanitizationOptions);

    const inlineCSSFunc = inlineCSS ? safeInlineStyles : (html: string) => html;

    return pipe(inlineCSSFunc, sanitizeHTML, removePlaceholderParagraphs)(html);
  }
}
